import * as Sentry from '@sentry/react-native';

type ScopeCallback = Parameters<typeof Sentry.captureException>[1];

const safeStringify = (value: unknown): string => {
  try {
    const str = JSON.stringify(value);
    return str ?? String(value ?? 'Unknown error');
  } catch {
    return String(value ?? 'Unknown error');
  }
};

const captureException = (error: unknown, context?: ScopeCallback) => {
  try {
    if (error instanceof Error) {
      Sentry.captureException?.(error, context);
      return;
    }

    const description =
      typeof error === 'string'
        ? error
        : error == null
          ? 'Unknown error'
          : safeStringify(error);

    const synthetic = new Error(description);
    Sentry.captureException?.(synthetic, context);
  } catch (err) {
    console.error('Sentry captureException failed', err);
  }
};

const captureMessage = (message: string, level?: Sentry.SeverityLevel) => {
  try {
    Sentry.captureMessage?.(message, level);
  } catch (err) {
    console.error('Sentry captureMessage failed', err);
  }
};

const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  try {
    Sentry.addBreadcrumb?.(breadcrumb);
  } catch (err) {
    console.error('Sentry addBreadcrumb failed', err);
  }
};

/**
 * Attach the authenticated user to the Sentry scope so crash/error events carry
 * who hit them. Pass `null` to clear (logout). `sendDefaultPii` is already on, so
 * email is acceptable here; we never attach passwords/tokens.
 */
const setUserContext = (
  user: { id?: string | number | null; email?: string | null } | null,
) => {
  try {
    if (user == null) {
      Sentry.setUser?.(null);
      return;
    }
    Sentry.setUser?.({
      ...(user.id != null ? { id: String(user.id) } : {}),
      ...(user.email ? { email: user.email } : {}),
    });
  } catch (err) {
    console.error('Sentry setUserContext failed', err);
  }
};

/**
 * Tag events with the active company so support can pivot a Sentry issue to a
 * tenant. Pass `null` (or an id-less object) to clear the tags.
 */
const setCompanyContext = (
  company: { id?: number | null; name?: string | null } | null,
) => {
  try {
    if (company == null || company.id == null) {
      Sentry.setTag?.('company.id', undefined);
      Sentry.setTag?.('company.name', undefined);
      return;
    }
    Sentry.setTag?.('company.id', String(company.id));
    Sentry.setTag?.('company.name', company.name ?? undefined);
  } catch (err) {
    console.error('Sentry setCompanyContext failed', err);
  }
};

/** Clear all per-session identity context. Call on logout. */
const clearContext = () => {
  try {
    Sentry.setUser?.(null);
    Sentry.setTag?.('company.id', undefined);
    Sentry.setTag?.('company.name', undefined);
  } catch (err) {
    console.error('Sentry clearContext failed', err);
  }
};

export default {
  captureException,
  captureMessage,
  addBreadcrumb,
  setUserContext,
  setCompanyContext,
  clearContext,
};

